import { Injectable, signal, computed, WritableSignal, Signal } from '@angular/core';
import { BarcodeFormat } from '@zxing/library';

@Injectable({
  providedIn: 'root'
})
export class BarcodeScannerService {

  // --- Private Writable Signals für internen Zustand ---
  private _isScanning: WritableSignal<boolean> = signal(false);
  private _isLoadingDevices: WritableSignal<boolean> = signal(false);
  private _currentDevice: WritableSignal<MediaDeviceInfo | undefined> = signal(undefined);
  private _availableDevices: WritableSignal<MediaDeviceInfo[]> = signal([]);
  private _hasPermission: WritableSignal<boolean> = signal(false);
  private _lastScanResult: WritableSignal<string | null> = signal(null); // Letztes Ergebnis
  private _currentScanTarget: WritableSignal<string | null> = signal(null);
  private _scanError: WritableSignal<Error | null> = signal(null);

  // --- Öffentliche Readonly Signals für Komponenten ---
  readonly isScanning: Signal<boolean> = this._isScanning.asReadonly();
  readonly isLoadingDevices: Signal<boolean> = this._isLoadingDevices.asReadonly();
  readonly currentDevice: Signal<MediaDeviceInfo | undefined> = this._currentDevice.asReadonly();
  readonly availableDevices: Signal<MediaDeviceInfo[]> = this._availableDevices.asReadonly();
  readonly hasPermission: Signal<boolean> = this._hasPermission.asReadonly();
  readonly lastScanResult: Signal<string | null> = this._lastScanResult.asReadonly();
  readonly currentScanTarget: Signal<string | null> = this._currentScanTarget.asReadonly();
  readonly scanError: Signal<Error | null> = this._scanError.asReadonly();

  // --- Computed Signal für hasDevices ---
  readonly hasDevices: Signal<boolean> = computed(() => this._availableDevices().length > 0);

  // --- Konfiguration ---
  readonly allowedFormats: BarcodeFormat[] = [ // Behalte dies als Array
    BarcodeFormat.EAN_13, BarcodeFormat.CODE_128, BarcodeFormat.QR_CODE, BarcodeFormat.DATA_MATRIX, BarcodeFormat.CODE_39
    // BarcodeFormat.CODE_93, BarcodeFormat.ITF, BarcodeFormat.AZTEC, BarcodeFormat.PDF_417, BarcodeFormat.CODABAR,
    // BarcodeFormat.UPC_A, BarcodeFormat.UPC_E, BarcodeFormat.RSS_14, BarcodeFormat.RSS_EXPANDED, BarcodeFormat.MAXICODE,
    // BarcodeFormat.UPC_EAN_EXTENSION, BarcodeFormat.EAN_8
  ];

  constructor() {
    // Initialen Berechtigungsstatus prüfen
    this.checkInitialPermission();
  }

  private async checkInitialPermission(): Promise<void> {
      try {
          // Prüfe, ob mediaDevices überhaupt verfügbar ist (wichtig für SSR oder Tests)
          if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
              console.warn("navigator.mediaDevices not available.");
              this._hasPermission.set(false);
              return;
          }
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          stream.getTracks().forEach(track => track.stop());
          this._hasPermission.set(true);
          console.log("Initial permission check: Granted");
          this.enumerateDevices(); // Nach Berechtigung Geräte suchen
      } catch (error) {
          this._hasPermission.set(false);
          console.log("Initial permission check: Not granted or no camera", error);
      }
  }

 private async enumerateDevices(): Promise<void> {
    try {
        if (typeof navigator === 'undefined' || !navigator.mediaDevices) return;
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(d => d.kind === 'videoinput');
        this.reportCamerasFound(videoDevices); // Intern melden
    } catch (error) {
        console.error("Error enumerating devices:", error);
        this.reportCamerasFound([]); // Leere Liste melden bei Fehler
    }
 }

  startScan(targetIdentifier: string): void {
    console.log(`Service: startScan called for: ${targetIdentifier}`);
    this._currentScanTarget.set(targetIdentifier);
    this._lastScanResult.set(null); // Reset previous result
    this._scanError.set(null);
    this._isLoadingDevices.set(false);

    if (!this.hasPermission()) { // Signalwert direkt abfragen
      console.log("Service: Permission not granted yet, requesting...");
      this.requestPermissionAndStartScan(targetIdentifier);
      return;
    }
    console.log("Service: Permission is granted.");

    this._isScanning.set(true); // Signal zum Anzeigen des Modals/Scanners

    if (!this.hasDevices()) { // Computed Signalwert abfragen
      console.warn("Service: Devices not yet confirmed. Scanner is enabled, waiting for component to report cameras...");
      this._isLoadingDevices.set(true);
    } else {
      console.log("Service: Devices already known.");
    }
  }

  stopScan(): void {
    console.log("Service: stopScan called.");
    this._isScanning.set(false);
    this._isLoadingDevices.set(false);
    this._currentScanTarget.set(null);
  }

  changeDevice(deviceId: string): void {
    console.log('Service: changeDevice called with:', deviceId);
    const selectedDevice = this.availableDevices().find(x => x.deviceId === deviceId); // Signalwert abfragen
    if (selectedDevice) {
      this._currentDevice.set(selectedDevice);
      console.log('Service: currentDevice updated to:', selectedDevice.label);
    } else {
      console.warn("Service: Selected device not found in available devices.");
    }
  }

  // --- Methoden für die Komponente, um Ergebnisse/Events vom Scanner zu melden ---

  reportCamerasFound(devices: MediaDeviceInfo[]): void {
    console.log("Service: reportCamerasFound called with:", devices);
    this._isLoadingDevices.set(false);
    this._availableDevices.set(devices); // Setzt das Signal, hasDevices wird automatisch neu berechnet

    const hasDevicesNow = devices.length > 0;
    console.log('Service: hasDevices computed to:', hasDevicesNow);

    if (hasDevicesNow) {
      const current = this.currentDevice(); // Signalwert abfragen
      const currentDeviceStillExists = current && devices.some(d => d.deviceId === current.deviceId);

      if (!current || !currentDeviceStillExists) {
        console.log("Service: Selecting new/first device.");
        const rearCamera = devices.find(device => /back|rear|environment/i.test(device.label));
        this._currentDevice.set(rearCamera || devices[0]);
        console.log("Service: Using device:", this.currentDevice()?.label);
      } else {
         console.log("Service: Current device still valid:", current.label);
      }
    } else {
      console.warn("Service: No cameras reported by component.");
      this._currentDevice.set(undefined);
      this._scanError.set(new Error("Keine Kamera gefunden."));
    }
  }

  reportPermissionResult(hasPermission: boolean): void {
     console.log("Service: reportPermissionResult called with:", hasPermission);
     const previouslyHadPermission = this.hasPermission(); // Alten Wert lesen
     this._hasPermission.set(hasPermission);
     if (!hasPermission) {
         console.error("Service: Camera permission denied or revoked.");
         this._scanError.set(new Error("Kameraberechtigung erforderlich."));
         if (this.isScanning()) { // Signalwert abfragen
             this.stopScan();
             alert("Kameraberechtigung wurde entzogen.");
         }
     } else {
          console.log("Service: Permission confirmed as granted.");
          this._scanError.set(null);
          if (!previouslyHadPermission) {
              console.log("Service: Permission newly granted, re-enumerating devices.");
              this._isLoadingDevices.set(true);
              this.enumerateDevices(); // Geräte neu suchen
          }
     }
  }

  reportScanSuccess(resultString: string): void {
    console.log(`Service: reportScanSuccess called with: ${resultString}`);
    this._lastScanResult.set(resultString); // Letztes Ergebnis speichern
    this.stopScan(); // Scan nach Erfolg automatisch stoppen
  }

  reportScanError(error: Error): void {
    console.log("Service: reportScanError called with:", error);
     if (error && error.message && !error.message.includes('undefined')) {
        this._scanError.set(error);
        console.error('Service Scan Error:', error);
    } else {
        this._scanError.set(null);
    }
  }

  // --- Private Hilfsmethoden ---

  private requestPermissionAndStartScan(targetIdentifier: string): void {
    alert("Bitte erteilen Sie die Kameraberechtigung.");
    // Prüfe, ob mediaDevices verfügbar ist
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
        console.error("Cannot request permission: navigator.mediaDevices not available.");
        this.reportPermissionResult(false);
        alert("Ihr Browser unterstützt den Kamerazugriff nicht oder die Seite ist nicht sicher (HTTPS).");
        return;
    }
    navigator.mediaDevices.getUserMedia({ video: true })
      .then((stream) => {
          stream.getTracks().forEach(track => track.stop());
          console.log("Service: Permission granted after request.");
          this.reportPermissionResult(true);
          setTimeout(() => this.startScan(targetIdentifier), 100);
      })
      .catch(err => {
          console.error("Service: Error requesting permission:", err);
          this.reportPermissionResult(false);
          alert("Kameraberechtigung wurde nicht erteilt.");
      });
  }

   // Optional: Manuelle Prüfmethode für Debugging
   async manualDeviceCheck() {
      try {
          console.log("Manual Check: Requesting permissions...");
          if (typeof navigator === 'undefined' || !navigator.mediaDevices) throw new Error("mediaDevices not available");
          await navigator.mediaDevices.getUserMedia({ video: true }).then(s => s.getTracks().forEach(t=>t.stop()));
          console.log("Manual Check: Permissions OK. Enumerating devices...");
          const devices = await navigator.mediaDevices.enumerateDevices();
          console.log("Manual Check: Devices Found:", devices);
          const videoDevices = devices.filter(device => device.kind === 'videoinput');
          alert(`Manual Check: Found ${videoDevices.length} video devices.`);
          this.reportCamerasFound(videoDevices); // Update service state
          this.reportPermissionResult(true);
      } catch (err) {
          console.error("Manual Check: Error:", err);
          alert("Manual Check: Error accessing devices: " + err);
          this.reportPermissionResult(false);
          this.reportCamerasFound([]);
      }
  }
}